import axios from "axios";
import { apiClient } from "../../lib/apiClient";
import api from "../../lib/api";
import { API_BASE_URL } from "../../lib/env";
import type {
  AcceptInvitePayload,
  AcceptInviteResponse,
  CurrentUserResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SignInResponse,
  ValidateInviteResponse,
} from "../types";
import {
  normalizeAuthEmail,
  normalizeFullName,
} from "../../components/auth/utils/normalizeCredentials";

export const AuthService = {
  async validateInvite(token: string) {
    return await apiClient.get<ValidateInviteResponse>(
      "/api/v1/auth/invite/validate",
      {
        params: { token },
      },
    );
  },

  async acceptInvite(payload: AcceptInvitePayload) {
    const body: AcceptInvitePayload = {
      ...payload,
      full_name: normalizeFullName(payload.full_name),
    };
    return await apiClient.post<AcceptInviteResponse, AcceptInvitePayload>(
      "/api/v1/auth/invite/accept",
      body,
    );
  },

  async signIn(email: string, password: string, rememberMe: boolean) {
    const formData = new URLSearchParams();
    formData.append("username", normalizeAuthEmail(email));
    formData.append("password", password);

    const response = await api.post<{
      access_token: string;
      token_type: "bearer";
    }>("/api/v1/auth/login", formData, {
      params: { remember_me: rememberMe ?? false },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    return {
      accessToken: response.data.access_token,
      tokenType: response.data.token_type,
    } satisfies SignInResponse;
  },

  async fetchCurrentUser() {
    return await apiClient.get<CurrentUserResponse>("/api/v1/users/me");
  },

  async logout() {
    // Cookie identifies the session — no body needed.
    return await apiClient.post<{ message: string }>("/api/v1/auth/logout");
  },

  async refreshToken() {
    // Bare axios, not `api`: `api`'s response interceptor calls back into
    // the shared refresh lock (authRefresh.ts) on a 401, and this request
    // is that lock's own in-flight call — routing it through `api` would
    // deadlock waiting on itself. Cookie identifies the session, so no body.
    const response = await axios.post<{ access_token: string }>(
      `${API_BASE_URL}/api/v1/auth/refresh`,
      undefined,
      { withCredentials: true, headers: { "Content-Type": "application/json" } },
    );
    return response.data.access_token;
  },

  async forgotPassword(payload: ForgotPasswordRequest) {
    return await apiClient.post<ForgotPasswordResponse, ForgotPasswordRequest>(
      "/api/v1/auth/forgot-password",
      {
        email: normalizeAuthEmail(payload.email),
      },
    );
  },

  async resetPassword(payload: ResetPasswordRequest) {
    return await apiClient.post<ResetPasswordResponse, ResetPasswordRequest>(
      "/api/v1/auth/reset-password",
      {
        ...payload,
        email: normalizeAuthEmail(payload.email),
      },
    );
  },

  async changePassword(payload: {
    current_password: string;
    new_password: string;
    confirm_new_password: string;
  }) {
    return await apiClient.post<{ message: string }, typeof payload>(
      "/api/v1/users/me/password",
      payload,
    );
  },

  async verifyResetCode(payload: { email: string; code: string }) {
    return await apiClient.post<
      { message: string },
      { email: string; code: string }
    >("/api/v1/auth/verify-reset-code", {
      email: normalizeAuthEmail(payload.email),
      code: payload.code,
    });
  },
};
